const { fetchJSON, getDefaultRequestConfig, getElement } = window.BAO.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some(url => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
        {
          form,
          formData,
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      if (window.location.host === 'shop-us.mercedesamgf1.com' && window.fbq) {
        window.fbq('track', 'AddToCart')
      }

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
      items,
      attributes,
      form,
    })

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
        items,
        state,
        form,
      })

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
      return err
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { key },
    )
    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    while (keys.length > 0) {
      const key = keys.shift()
      const isLastKey = keys.length === 0
      const body = {
        id: key,
        quantity: 0,
      }

      if (isLastKey && sections.length) {
        body.sections = sections
        body.sections_url = sectionsUrl
      }

      try {
        const state = await this.cartChange(body)

        if (isLastKey) {
          window.BAO.dispatchEvent(
            window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
            {
              state,
            },
          )

          window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
            state,
          })

          return state
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    properties,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} discount Comma-separated discount codes
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartDiscount (
    discount,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_DISCOUNT,
      {
        discount,
      },
    )

    const body = { discount }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_DISCOUNT,
        {
          discount,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }
}

window.BAO.Cart = new BaoCart()
